import { currency, percent, rate, tenure } from '../lib/format.js';

/**
 * CollateralPanel — the mutual-fund side of the selected plan.
 *
 * This is the panel that explains why buying through 1Fi differs from any other
 * EMI offer: the shopper pledges units rather than selling them, so the holding
 * continues to track the market while the loan is repaid.
 *
 * Two rules are applied deliberately in the presentation:
 *
 *   1. Interest paid and projected growth are shown side by side but never netted
 *      into a single "profit" figure. Interest is certain; growth is not, and
 *      combining them would imply a guaranteed return.
 *   2. The growth number always carries its assumed rate inline, so the
 *      assumption is never invisible.
 */

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <path d="M7 17 17 7M17 7h-6M17 7v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Figure({ label, value, hint, tone = 'neutral' }) {
  const tones = {
    neutral: 'text-ink-950',
    growth: 'text-growth-700',
    cost: 'text-ink-950',
  };
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className={`mt-1 text-xl font-bold ${tones[tone]}`}>{value}</dd>
      {hint && <p className="mt-0.5 text-xs leading-relaxed text-ink-500">{hint}</p>}
    </div>
  );
}

export default function CollateralPanel({ plan }) {
  if (!plan?.collateral) return null;

  const { collateral, comparison, totalInterest, tenureMonths, isNoCostEmi } = plan;

  return (
    <section
      aria-labelledby="collateral-heading"
      className="overflow-hidden rounded-card border border-growth-200 bg-gradient-to-br from-growth-50 via-white to-brand-50/50"
    >
      <div className="border-b border-growth-200/70 px-5 py-4">
        <h3 id="collateral-heading" className="flex items-center gap-2 font-semibold text-ink-950">
          <span className="grid size-7 place-items-center rounded-lg bg-growth-600 text-white">
            <ArrowIcon />
          </span>
          Your investments keep working
        </h3>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-600">
          You pledge units as security — nothing is redeemed. Over {tenure(tenureMonths)} they stay exposed to the
          market.
        </p>
      </div>

      <dl className="grid grid-cols-2 gap-5 px-5 py-5">
        <Figure
          label="Units to pledge"
          value={currency(collateral.pledgeRequired)}
          hint={`At ${percent(collateral.ltv, { decimals: 0 })} loan-to-value on ${collateral.fundType} funds`}
        />
        <Figure
          label={`Value after ${tenureMonths} mo`}
          value={currency(collateral.projectedPledgeValue)}
          tone="growth"
          hint={`Illustrative, at ${rate(collateral.assumedCagr)} p.a. — not guaranteed`}
        />
        <Figure
          label="Potential growth kept"
          value={`+ ${currency(collateral.projectedGrowth)}`}
          tone="growth"
          hint="Upside you would have forfeited by selling the units instead"
        />
        <Figure
          label="Interest you pay"
          value={isNoCostEmi ? 'Nil' : currency(totalInterest)}
          hint={isNoCostEmi ? 'This is a genuine no-cost plan' : 'Certain cost, unlike the projection above'}
        />
      </dl>

      {comparison?.savingsVsCreditCard > 0 && (
        <div className="border-t border-growth-200/70 bg-white/60 px-5 py-4">
          <p className="text-sm leading-relaxed text-ink-700">
            Financing this on a credit card at {rate(42)} p.a. would cost roughly{' '}
            <strong className="font-semibold text-ink-950">{currency(comparison.creditCardInterest)}</strong> in
            interest. This secured plan saves about{' '}
            <strong className="font-semibold text-growth-700">
              {currency(comparison.savingsVsCreditCard)}
            </strong>
            .
          </p>
        </div>
      )}
    </section>
  );
}
